import { BillOfMaterialsUnitFormModel } from '../../bill-of-materials/billOfMaterialsUnitFormModel';
import {
  CreateUpdateBillOfMaterialsItemDto,
  mapBillOfMaterialsItemFormModelToDto,
} from '../../bill-of-materials/createUpdateBillOfMaterialsItemDto';
import {
  CreateUpdateBillOfMaterialsOptionalExtraDto,
  mapBillOfMaterialsOptionalExtraFormModelToDto,
} from '../../bill-of-materials/createUpdateBillOfMaterialsOptionalExtraDto';
import {
  ViewOrEditQuoteBillOfMaterialsFormModel,
  ManualDiscountTypeCode,
} from './viewOrEditQuoteBillOfMaterialsFormModel';

export type UpdateQuoteBillOfMaterialsCommand = {
  quoteId: number;
  isUsingLegacyPricingModel: boolean;
  customerTypeId: number | null;
  underfloorHeatingQuoteDescription: string;
  underfloorHeatingDiscountPercentage: number | null;
  underfloorHeatingManualDiscountAmount: number;
  underfloorHeatingManualDiscountTypeCode: ManualDiscountTypeCode;
  heatPumpQuoteDescription: string;
  heatPumpDiscountPercentage: number | null;
  heatPumpManualDiscountAmount: number;
  heatPumpManualDiscountTypeCode: ManualDiscountTypeCode;
  solarQuoteDescription: string;
  solarDiscountPercentage: number | null;
  solarManualDiscountAmount: number;
  solarManualDiscountTypeCode: ManualDiscountTypeCode;
  units: Array<UpdateQuoteBillOfMaterialsUnitDto>;
  optionalExtras: Array<CreateUpdateBillOfMaterialsOptionalExtraDto>;
  selectedGuidanceText: Array<SelectedGuidanceText>;
};

type UpdateQuoteBillOfMaterialsUnitDto = {
  billOfMaterialsUnitId: number;
  underfloorHeatingDescription: string;
  heatPumpDescription: string;
  solarDescription: string;
  items: Array<CreateUpdateBillOfMaterialsItemDto>;
};

type SelectedGuidanceText = {
  guidanceTextTemplateId: number;
  isCustomised: boolean;
  customTitle: string | null;
  customBody: string | null;
};

export const mapEditQuoteBillOfMaterialsFormModelToCommand = (
  {
    isUsingLegacyPricingModel,
    customerTypeId,
    underfloorHeatingQuoteDescription,
    underfloorHeatingDiscountPercentage,
    underfloorHeatingManualDiscountAmount,
    underfloorHeatingManualDiscountTypeCode,
    heatPumpQuoteDescription,
    heatPumpDiscountPercentage,
    heatPumpManualDiscountAmount,
    heatPumpManualDiscountTypeCode,
    solarQuoteDescription,
    solarDiscountPercentage,
    solarManualDiscountAmount,
    solarManualDiscountTypeCode,
    units,
    underfloorHeatingControlOptionalExtras,
    underfloorHeatingFloorConstructionOptionalExtras,
    underfloorHeatingCylinderOptionalExtras,
    heatPumpOptionalExtras,
    solarOptionalExtras,
    underfloorHeatingGuidanceText,
    heatPumpGuidanceText,
    solarGuidanceText,
  }: ViewOrEditQuoteBillOfMaterialsFormModel,
  quoteId: number,
): UpdateQuoteBillOfMaterialsCommand => ({
  quoteId,
  isUsingLegacyPricingModel,
  customerTypeId,
  underfloorHeatingQuoteDescription: underfloorHeatingQuoteDescription ?? '',
  underfloorHeatingDiscountPercentage: isUsingLegacyPricingModel
    ? Number(underfloorHeatingDiscountPercentage) ?? null
    : null,
  underfloorHeatingManualDiscountAmount: Number(underfloorHeatingManualDiscountAmount),
  underfloorHeatingManualDiscountTypeCode,
  heatPumpQuoteDescription: heatPumpQuoteDescription ?? '',
  heatPumpDiscountPercentage: isUsingLegacyPricingModel ? Number(heatPumpDiscountPercentage) ?? null : null,
  heatPumpManualDiscountAmount: Number(heatPumpManualDiscountAmount),
  heatPumpManualDiscountTypeCode,
  solarQuoteDescription: solarQuoteDescription ?? '',
  solarDiscountPercentage: isUsingLegacyPricingModel ? Number(solarDiscountPercentage) ?? null : null,
  solarManualDiscountAmount: Number(solarManualDiscountAmount),
  solarManualDiscountTypeCode,
  units: units.map(mapBillOfMaterialsUnitFormModelToQuoteDto),
  optionalExtras: [
    ...underfloorHeatingControlOptionalExtras.map(extra =>
      mapBillOfMaterialsOptionalExtraFormModelToDto(extra, 'UnderfloorHeating', 'Control'),
    ),
    ...underfloorHeatingFloorConstructionOptionalExtras.map(extra =>
      mapBillOfMaterialsOptionalExtraFormModelToDto(extra, 'UnderfloorHeating', 'FloorConstruction'),
    ),
    ...underfloorHeatingCylinderOptionalExtras.map(extra =>
      mapBillOfMaterialsOptionalExtraFormModelToDto(extra, 'UnderfloorHeating', 'Cylinder'),
    ),
    ...heatPumpOptionalExtras.map(extra => mapBillOfMaterialsOptionalExtraFormModelToDto(extra, 'HeatPump', null)),
    ...solarOptionalExtras.map(extra => mapBillOfMaterialsOptionalExtraFormModelToDto(extra, 'Solar', null)),
  ],
  selectedGuidanceText: [...underfloorHeatingGuidanceText, ...heatPumpGuidanceText, ...solarGuidanceText]
    .filter(guidanceText => guidanceText.isSelected)
    .map(guidanceText => ({
      guidanceTextTemplateId: guidanceText.guidanceTextTemplateId,
      isCustomised: guidanceText.isCustomised,
      customTitle: guidanceText.isCustomised ? guidanceText.customTitle : null,
      customBody: guidanceText.isCustomised ? guidanceText.customBody : null,
    })),
});

const mapBillOfMaterialsUnitFormModelToQuoteDto = ({
  billOfMaterialsUnitId,
  underfloorHeatingDescription,
  heatPumpDescription,
  solarDescription,
  underfloorHeatingItems,
  heatPumpItems,
  solarItems,
}: BillOfMaterialsUnitFormModel): UpdateQuoteBillOfMaterialsUnitDto => ({
  billOfMaterialsUnitId,
  underfloorHeatingDescription: underfloorHeatingDescription ?? '',
  heatPumpDescription: heatPumpDescription ?? '',
  solarDescription: solarDescription ?? '',
  items: [
    ...underfloorHeatingItems.map((formModel, index) =>
      mapBillOfMaterialsItemFormModelToDto(formModel, index, 'UnderfloorHeating'),
    ),
    ...heatPumpItems.map((formModel, index) => mapBillOfMaterialsItemFormModelToDto(formModel, index, 'HeatPump')),
    ...solarItems.map((formModel, index) => mapBillOfMaterialsItemFormModelToDto(formModel, index, 'Solar')),
  ],
});
